import type {
  ProFeeAuditAlert,
  ProFeeAuditAlertsResponse,
  ProFeeAuditRecord,
} from "../types/pro-fee-audit";

const AUDIT_KINDS = new Set([
  "ptp",
  "ncci",
  "mcd",
  "em-dx",
  "mue",
  "ioce",
]);

const NCCI_STATUS_LABEL: Record<string, string> = {
  bundled: "Bundled",
  separately_reportable: "Separately reportable",
  modifier_required: "Modifier required",
  unit_adjustment_needed: "Unit adjustment needed",
};

const MCD_REASON_LABEL: Record<string, string> = {
  FAILED: "Failed",
  NONCOVERED: "Noncovered",
  NOT_COVERED: "Not covered",
  CONFIRM_AND_MAP: "Confirm and map",
  NOT_MAPPED: "Not mapped",
  NOT_SELECTED: "Not selected",
};

const EM_DX_REASON_LABEL: Record<string, string> = {
  EMPTY_MAPPING: "Empty mapping",
  UNCONFIRMED_DIAGNOSIS: "Unconfirmed diagnosis",
};

export type ProFeeAuditLine = {
  key: string;
  kind: ProFeeAuditAlert["kind"];
  kindLabel: string;
  codes: string;
  title: string;
  detail: string;
  badge?: string;
  dateOfService?: string;
  savedAt?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const asText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const toAuditDateOfService = (
  value?: string | null
): string | undefined => {
  if (!value) return undefined;
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

const isAuditAlert = (value: unknown): value is ProFeeAuditAlert =>
  isRecord(value) &&
  typeof value.kind === "string" &&
  AUDIT_KINDS.has(value.kind);

const emptyResponse = (
  extra?: Partial<ProFeeAuditAlertsResponse>
): ProFeeAuditAlertsResponse => ({
  success: extra?.success ?? false,
  providerNpi: extra?.providerNpi ?? "",
  count: extra?.count ?? 0,
  audits: extra?.audits ?? [],
  error: extra?.error,
});

const normalizeAudit = (value: unknown): ProFeeAuditRecord | null => {
  if (!isRecord(value)) return null;
  const alerts = Array.isArray(value.alerts)
    ? value.alerts.filter(isAuditAlert)
    : [];
  const caseId = asText(value.caseId);
  if (!caseId && !alerts.length && !asText(value.mrn)) return null;
  const alertCount =
    typeof value.alertCount === "number" && Number.isFinite(value.alertCount)
      ? value.alertCount
      : alerts.length;
  return {
    caseId: caseId || `audit-${alerts.length}`,
    projectId: asText(value.projectId) || undefined,
    reviewId: asText(value.reviewId) || undefined,
    revisionNo:
      typeof value.revisionNo === "number" ? value.revisionNo : undefined,
    patientName: asText(value.patientName) || undefined,
    mrn: asText(value.mrn) || undefined,
    dateOfService: asText(value.dateOfService) || undefined,
    savedAt: asText(value.savedAt) || undefined,
    alertCount,
    alerts,
    updatedAt: asText(value.updatedAt) || undefined,
  };
};

export const normalizeProFeeAuditAlertsResponse = (
  payload: unknown
): ProFeeAuditAlertsResponse => {
  if (!isRecord(payload)) {
    return emptyResponse({ error: "Unexpected audit alerts response" });
  }

  const error = asText(payload.error);
  if (payload.success === false) {
    return emptyResponse({
      success: false,
      providerNpi: asText(payload.providerNpi),
      error: error || "Unable to load coding alerts",
    });
  }

  const audits = Array.isArray(payload.audits)
    ? payload.audits
        .map(normalizeAudit)
        .filter((audit): audit is ProFeeAuditRecord => Boolean(audit))
    : [];

  return {
    success: payload.success !== false,
    providerNpi: asText(payload.providerNpi),
    count:
      typeof payload.count === "number" && Number.isFinite(payload.count)
        ? payload.count
        : audits.length,
    audits,
  };
};

export const flattenProFeeAuditAlerts = (
  audits: ProFeeAuditRecord[] | undefined
): ProFeeAuditAlert[] =>
  (audits ?? []).reduce<ProFeeAuditAlert[]>(
    (all, audit) => all.concat(audit.alerts ?? []),
    []
  );

export const countProFeeAuditAlerts = (
  response?: ProFeeAuditAlertsResponse | null
): number => {
  if (!response?.success) return 0;
  return flattenProFeeAuditAlerts(response.audits).length;
};

const joinDetail = (parts: Array<string | undefined>): string =>
  parts
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(" — ");

const labelFor = (
  value: string | undefined,
  labels: Record<string, string>
): string | undefined => {
  if (!value) return undefined;
  return labels[value] || value;
};

const pairCodes = (left?: string, right?: string): string => {
  const a = left?.trim();
  const b = right?.trim();
  if (a && b) return `${a}. (+ ${b})`;
  if (a) return a;
  if (b) return b;
  return "—";
};

export const formatProFeeAuditAlert = (
  alert: ProFeeAuditAlert,
  index: number
): ProFeeAuditLine => {
  const kindLabel =
    alert.kind === "ptp"
      ? "NCCI PTP"
      : alert.kind === "ncci"
        ? "NCCI Bundling"
        : alert.kind === "mcd"
          ? "MCD"
          : alert.kind === "em-dx"
            ? "Diagnosis Mapping"
            : alert.kind === "mue"
              ? "MUE"
              : "I/OCE";

  if (alert.kind === "ptp") {
    const allowed =
      alert.modifierIndicator === 0
        ? "Modifier not allowed"
        : alert.modifierIndicator === 1
          ? alert.allowedModifiers?.length
            ? `Modifier Required: ${alert.allowedModifiers.join(", ")}`
            : "Modifier Required"
          : undefined;
    return {
      key: `ptp-${index}-${alert.cpt}-${alert.componentCode}`,
      kind: alert.kind,
      kindLabel,
      codes: pairCodes(alert.cpt, alert.componentCode),
      title: `NCCI PTP: ${pairCodes(alert.cpt, alert.componentCode)}`,
      detail: alert.rationale || "",
      badge: allowed,
    };
  }

  if (alert.kind === "ncci") {
    return {
      key: `ncci-${index}-${alert.cpt}-${alert.bundledCode}`,
      kind: alert.kind,
      kindLabel,
      codes: pairCodes(alert.cpt, alert.bundledCode),
      title: `NCCI Bundling: ${pairCodes(alert.cpt, alert.bundledCode)}`,
      detail: `${alert.rationale?.trim() ?? ""} Unless modifier is added`.trim(),
      badge: labelFor(alert.status, NCCI_STATUS_LABEL),
    };
  }

  if (alert.kind === "mcd") {
    return {
      key: `mcd-${index}-${alert.cpt}-${alert.diagnosis ?? ""}`,
      kind: alert.kind,
      kindLabel,
      codes: alert.diagnosis
        ? pairCodes(alert.cpt, alert.diagnosis)
        : alert.cpt || "—",
      title: alert.diagnosis
        ? `MCD: ${pairCodes(alert.cpt, alert.diagnosis)}`
        : `MCD: ${alert.cpt || "—"}`,
      detail: alert.description || "",
      badge: labelFor(alert.reason, MCD_REASON_LABEL),
    };
  }

  if (alert.kind === "em-dx") {
    const unconfirmed = alert.diagnosis
      ? `Unconfirmed ${alert.diagnosis}`
      : "Unconfirmed diagnosis";
    return {
      key: `em-dx-${index}-${alert.cpt}-${alert.diagnosis ?? ""}`,
      kind: alert.kind,
      kindLabel,
      codes: alert.diagnosis
        ? pairCodes(alert.cpt, alert.diagnosis)
        : alert.cpt || "—",
      title: alert.diagnosis
        ? `Diagnosis Mapping: ${pairCodes(alert.cpt, alert.diagnosis)}`
        : `Diagnosis Mapping: ${alert.cpt || "—"}`,
      detail:
        alert.reason === "EMPTY_MAPPING"
          ? alert.description || "Empty diagnosis mapping"
          : joinDetail([unconfirmed, alert.description]),
      badge: labelFor(alert.reason, EM_DX_REASON_LABEL),
    };
  }

  if (alert.kind === "mue") {
    const validation = alert.validation;
    return {
      key: `mue-${index}-${alert.cpt}-${alert.dos}`,
      kind: alert.kind,
      kindLabel,
      codes: alert.cpt || "—",
      title: `MUE: ${alert.cpt || "—"} on ${alert.dos || "—"}`,
      detail: joinDetail([
        `Billed ${alert.totalUnits ?? "—"} vs max ${validation?.mue_limit ?? "—"}`,
        validation?.message,
        validation?.rationale && validation.rationale !== validation.message
          ? validation.rationale
          : undefined,
      ]),
      badge: validation?.mai || undefined,
    };
  }

  return {
    key: `ioce-${index}-${alert.editCode}-${alert.subject}`,
    kind: alert.kind,
    kindLabel,
    codes: alert.subject
      ? pairCodes(alert.editCode, alert.subject)
      : alert.editCode || "—",
    title: `I/OCE ${alert.editCode || "—"} on ${alert.subject || "—"}`,
    detail: joinDetail([alert.editName, alert.description]),
    badge: alert.scope || undefined,
  };
};

export const flattenProFeeAuditRows = (
  audits: ProFeeAuditRecord[] | undefined
): ProFeeAuditLine[] => {
  const rows: ProFeeAuditLine[] = [];
  (audits ?? []).forEach((audit) => {
    (audit.alerts ?? []).forEach((alert) => {
      rows.push({
        ...formatProFeeAuditAlert(alert, rows.length),
        dateOfService: audit.dateOfService,
        savedAt: audit.savedAt || audit.updatedAt,
      });
    });
  });
  return rows;
};
